import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, CircleNotch, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { GroupHeading } from "@/components/customer/caravan/buildFields";

const CLAIM_STATUS_STYLES = {
  "approved": "bg-[#73bf44]/15 text-[#73bf44] border-[#73bf44]/30",
  "completed": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "declined": "bg-red-500/15 text-red-400 border-red-500/30",
  "further information required": "bg-orange-500/15 text-orange-400 border-orange-500/30",
  "invoiced": "bg-teal-500/15 text-teal-300 border-teal-500/30",
  "pending assessment": "bg-neutral-500/15 text-neutral-300 border-neutral-500/40",
};
const claimStatusStyle = (s) => CLAIM_STATUS_STYLES[(s || "").toLowerCase()] || "bg-sky-500/15 text-sky-300 border-sky-500/30";
function fmtClaimDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

export function useWarrantyClaims(chassis) {
  const [state, setState] = useState({ loading: true, error: false, claims: [] });
  useEffect(() => {
    if (!chassis) return undefined;
    let alive = true;
    setState({ loading: true, error: false, claims: [] });
    api
      .get("/warranty/status", { params: { chassis }, timeout: 30000 })
      .then(({ data }) => { if (alive) setState({ loading: false, error: false, claims: data.claims || [] }); })
      .catch(() => { if (alive) setState({ loading: false, error: true, claims: [] }); });
    return () => { alive = false; };
  }, [chassis]);
  return state;
}

function ClaimRow({ claim, i }) {
  return (
    <div data-testid={`warranty-history-item-${i}`} className="flex flex-col gap-2 bg-[#141414] border border-neutral-800 rounded-xl px-4 py-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-neutral-300">Submitted <span className="text-white font-medium">{fmtClaimDate(claim.submitted_on)}</span></span>
        <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${claimStatusStyle(claim.status)}`}>{claim.status}</span>
      </div>
      {claim.status.toLowerCase() === "approved" && claim.approval_number && (
        <div className="flex items-center gap-2 text-xs text-neutral-400" data-testid={`warranty-history-approval-${i}`}>
          <span>Approval number:</span>
          <span className="font-mono text-[#73bf44] font-semibold">{claim.approval_number}</span>
        </div>
      )}
    </div>
  );
}

export function WarrantyHistory({ state }) {
  const navigate = useNavigate();
  const trackLink = (
    <button onClick={() => navigate("/claim-status")} data-testid="warranty-history-track-link" className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#73bf44] transition-colors">
      Track my claim <CaretRight size={12} weight="bold" />
    </button>
  );
  return (
    <section data-testid="warranty-history" className="bg-[#0F0F0F] border border-neutral-800 rounded-2xl p-5 sm:p-6 max-w-3xl">
      <GroupHeading icon={ShieldCheck} title="Warranty Claim History" aside={trackLink} />
      {state.loading ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500 py-2" data-testid="warranty-history-loading">
          <CircleNotch size={16} className="animate-spin" /> Checking the warranty system…
        </div>
      ) : state.error ? (
        <p className="text-sm text-neutral-500 py-2" data-testid="warranty-history-error">We couldn't load your claim history right now. Please try again shortly.</p>
      ) : state.claims.length === 0 ? (
        <div className="py-2" data-testid="warranty-history-empty">
          <p className="text-sm text-neutral-400">No warranty claims are on file for this caravan.</p>
          <button onClick={() => navigate("/warranty")} data-testid="warranty-history-submit-link" className="mt-3 inline-flex items-center gap-2 text-sm text-[#73bf44] hover:text-[#5fa338] transition-colors font-semibold">
            <ShieldCheck size={16} weight="fill" /> Submit a warranty claim
          </button>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="warranty-history-list">
          {state.claims.map((c, i) => <ClaimRow key={i} claim={c} i={i} />)}
        </div>
      )}
    </section>
  );
}
